/**
 * Static ISO 3166-1 alpha-2 country dataset with capital-city coordinates.
 *
 * Used by the per-workspace map configurator to default the map center to a
 * selected country's capital. Values come from a verified public dataset and
 * are embedded statically — there is NO runtime fetch.
 *
 * Localized country names are NOT stored here; they are derived at render time
 * via `Intl.DisplayNames` (see `useCountryOptions`). Order is not significant —
 * consumers sort by localized label.
 */

/**
 * A single country entry: its ISO 3166-1 alpha-2 code and the latitude/longitude
 * of its capital city.
 */
export interface CountryEntry {
  /** ISO 3166-1 alpha-2 country code (uppercase). */
  code: string;
  /** Capital city coordinates. */
  capital: {
    lat: number;
    lng: number;
  };
}

/**
 * Country list with capital coordinates (~195 sovereign states + select territories).
 */
export const COUNTRIES: readonly CountryEntry[] = [
  { code: 'AD', capital: { lat: 42.5063, lng: 1.5218 } }, // Andorra la Vella
  { code: 'AE', capital: { lat: 24.4539, lng: 54.3773 } }, // Abu Dhabi
  { code: 'AF', capital: { lat: 34.5553, lng: 69.2075 } }, // Kabul
  { code: 'AG', capital: { lat: 17.1175, lng: -61.8456 } }, // Saint John's
  { code: 'AI', capital: { lat: 18.2206, lng: -63.0686 } }, // The Valley
  { code: 'AL', capital: { lat: 41.3275, lng: 19.8187 } }, // Tirana
  { code: 'AM', capital: { lat: 40.1792, lng: 44.4991 } }, // Yerevan
  { code: 'AO', capital: { lat: -8.839, lng: 13.2894 } }, // Luanda
  { code: 'AR', capital: { lat: -34.6037, lng: -58.3816 } }, // Buenos Aires
  { code: 'AT', capital: { lat: 48.2082, lng: 16.3738 } }, // Vienna
  { code: 'AU', capital: { lat: -35.2809, lng: 149.13 } }, // Canberra
  { code: 'AW', capital: { lat: 12.5092, lng: -70.0086 } }, // Oranjestad
  { code: 'AZ', capital: { lat: 40.4093, lng: 49.8671 } }, // Baku
  { code: 'BA', capital: { lat: 43.8563, lng: 18.4131 } }, // Sarajevo
  { code: 'BB', capital: { lat: 13.1132, lng: -59.5988 } }, // Bridgetown
  { code: 'BD', capital: { lat: 23.8103, lng: 90.4125 } }, // Dhaka
  { code: 'BE', capital: { lat: 50.8503, lng: 4.3517 } }, // Brussels
  { code: 'BF', capital: { lat: 12.3714, lng: -1.5197 } }, // Ouagadougou
  { code: 'BG', capital: { lat: 42.6977, lng: 23.3219 } }, // Sofia
  { code: 'BH', capital: { lat: 26.2285, lng: 50.586 } }, // Manama
  { code: 'BI', capital: { lat: -3.3614, lng: 29.3599 } }, // Gitega
  { code: 'BJ', capital: { lat: 6.4969, lng: 2.6283 } }, // Porto-Novo
  { code: 'BM', capital: { lat: 32.2949, lng: -64.7814 } }, // Hamilton
  { code: 'BN', capital: { lat: 4.9031, lng: 114.9398 } }, // Bandar Seri Begawan
  { code: 'BO', capital: { lat: -16.4897, lng: -68.1193 } }, // La Paz
  { code: 'BR', capital: { lat: -15.7942, lng: -47.8822 } }, // Brasilia
  { code: 'BS', capital: { lat: 25.0443, lng: -77.3504 } }, // Nassau
  { code: 'BT', capital: { lat: 27.4728, lng: 89.639 } }, // Thimphu
  { code: 'BW', capital: { lat: -24.6282, lng: 25.9231 } }, // Gaborone
  { code: 'BY', capital: { lat: 53.9006, lng: 27.559 } }, // Minsk
  { code: 'BZ', capital: { lat: 17.2514, lng: -88.759 } }, // Belmopan
  { code: 'CA', capital: { lat: 45.4215, lng: -75.6972 } }, // Ottawa
  { code: 'CD', capital: { lat: -4.4419, lng: 15.2663 } }, // Kinshasa
  { code: 'CF', capital: { lat: 4.3947, lng: 18.5582 } }, // Bangui
  { code: 'CG', capital: { lat: -4.2634, lng: 15.2429 } }, // Brazzaville
  { code: 'CH', capital: { lat: 46.948, lng: 7.4474 } }, // Bern
  { code: 'CI', capital: { lat: 6.8276, lng: -5.2893 } }, // Yamoussoukro
  { code: 'CL', capital: { lat: -33.4489, lng: -70.6693 } }, // Santiago
  { code: 'CM', capital: { lat: 3.848, lng: 11.5021 } }, // Yaounde
  { code: 'CN', capital: { lat: 39.9042, lng: 116.4074 } }, // Beijing
  { code: 'CO', capital: { lat: 4.711, lng: -74.0721 } }, // Bogota
  { code: 'CR', capital: { lat: 9.9281, lng: -84.0907 } }, // San Jose
  { code: 'CU', capital: { lat: 23.1136, lng: -82.3666 } }, // Havana
  { code: 'CV', capital: { lat: 14.933, lng: -23.5133 } }, // Praia
  { code: 'CY', capital: { lat: 35.1856, lng: 33.3823 } }, // Nicosia
  { code: 'CZ', capital: { lat: 50.0755, lng: 14.4378 } }, // Prague
  { code: 'DE', capital: { lat: 52.52, lng: 13.405 } }, // Berlin
  { code: 'DJ', capital: { lat: 11.5721, lng: 43.1456 } }, // Djibouti
  { code: 'DK', capital: { lat: 55.6761, lng: 12.5683 } }, // Copenhagen
  { code: 'DM', capital: { lat: 15.301, lng: -61.3878 } }, // Roseau
  { code: 'DO', capital: { lat: 18.4861, lng: -69.9312 } }, // Santo Domingo
  { code: 'DZ', capital: { lat: 36.7538, lng: 3.0588 } }, // Algiers
  { code: 'EC', capital: { lat: -0.1807, lng: -78.4678 } }, // Quito
  { code: 'EE', capital: { lat: 59.437, lng: 24.7536 } }, // Tallinn
  { code: 'EG', capital: { lat: 30.0444, lng: 31.2357 } }, // Cairo
  { code: 'ER', capital: { lat: 15.3229, lng: 38.925 } }, // Asmara
  { code: 'ES', capital: { lat: 40.4168, lng: -3.7038 } }, // Madrid
  { code: 'ET', capital: { lat: 9.145, lng: 38.7635 } }, // Addis Ababa
  { code: 'FI', capital: { lat: 60.1699, lng: 24.9384 } }, // Helsinki
  { code: 'FJ', capital: { lat: -18.1416, lng: 178.4419 } }, // Suva
  { code: 'FM', capital: { lat: 6.9248, lng: 158.1611 } }, // Palikir
  { code: 'FO', capital: { lat: 62.0079, lng: -6.7901 } }, // Torshavn
  { code: 'FR', capital: { lat: 48.8566, lng: 2.3522 } }, // Paris
  { code: 'GA', capital: { lat: 0.4162, lng: 9.4673 } }, // Libreville
  { code: 'GB', capital: { lat: 51.5074, lng: -0.1278 } }, // London
  { code: 'GD', capital: { lat: 12.0561, lng: -61.7488 } }, // Saint George's
  { code: 'GE', capital: { lat: 41.7151, lng: 44.8271 } }, // Tbilisi
  { code: 'GG', capital: { lat: 49.4567, lng: -2.5369 } }, // Saint Peter Port
  { code: 'GH', capital: { lat: 5.6037, lng: -0.187 } }, // Accra
  { code: 'GI', capital: { lat: 36.1408, lng: -5.3536 } }, // Gibraltar
  { code: 'GL', capital: { lat: 64.1836, lng: -51.7214 } }, // Nuuk
  { code: 'GM', capital: { lat: 13.4549, lng: -16.579 } }, // Banjul
  { code: 'GN', capital: { lat: 9.6412, lng: -13.5784 } }, // Conakry
  { code: 'GQ', capital: { lat: 3.7504, lng: 8.7371 } }, // Malabo
  { code: 'GR', capital: { lat: 37.9838, lng: 23.7275 } }, // Athens
  { code: 'GT', capital: { lat: 14.6349, lng: -90.5069 } }, // Guatemala City
  { code: 'GW', capital: { lat: 11.8817, lng: -15.6178 } }, // Bissau
  { code: 'GY', capital: { lat: 6.8013, lng: -58.1551 } }, // Georgetown
  { code: 'HN', capital: { lat: 14.0723, lng: -87.1921 } }, // Tegucigalpa
  { code: 'HR', capital: { lat: 45.815, lng: 15.9819 } }, // Zagreb
  { code: 'HT', capital: { lat: 18.5944, lng: -72.3074 } }, // Port-au-Prince
  { code: 'HU', capital: { lat: 47.4979, lng: 19.0402 } }, // Budapest
  { code: 'ID', capital: { lat: -6.2088, lng: 106.8456 } }, // Jakarta
  { code: 'IE', capital: { lat: 53.3498, lng: -6.2603 } }, // Dublin
  { code: 'IL', capital: { lat: 31.7683, lng: 35.2137 } }, // Jerusalem
  { code: 'IM', capital: { lat: 54.1509, lng: -4.4778 } }, // Douglas
  { code: 'IN', capital: { lat: 28.6139, lng: 77.209 } }, // New Delhi
  { code: 'IQ', capital: { lat: 33.3152, lng: 44.3661 } }, // Baghdad
  { code: 'IR', capital: { lat: 35.6892, lng: 51.389 } }, // Tehran
  { code: 'IS', capital: { lat: 64.1466, lng: -21.9426 } }, // Reykjavik
  { code: 'IT', capital: { lat: 41.9028, lng: 12.4964 } }, // Rome
  { code: 'JE', capital: { lat: 49.1858, lng: -2.1101 } }, // Saint Helier
  { code: 'JM', capital: { lat: 18.0179, lng: -76.8099 } }, // Kingston
  { code: 'JO', capital: { lat: 31.9454, lng: 35.9284 } }, // Amman
  { code: 'JP', capital: { lat: 35.6762, lng: 139.6503 } }, // Tokyo
  { code: 'KE', capital: { lat: -1.2921, lng: 36.8219 } }, // Nairobi
  { code: 'KG', capital: { lat: 42.8746, lng: 74.5698 } }, // Bishkek
  { code: 'KH', capital: { lat: 11.5564, lng: 104.9282 } }, // Phnom Penh
  { code: 'KI', capital: { lat: 1.3291, lng: 172.979 } }, // South Tarawa
  { code: 'KM', capital: { lat: -11.7172, lng: 43.2473 } }, // Moroni
  { code: 'KN', capital: { lat: 17.3026, lng: -62.7177 } }, // Basseterre
  { code: 'KP', capital: { lat: 39.0392, lng: 125.7625 } }, // Pyongyang
  { code: 'KR', capital: { lat: 37.5665, lng: 126.978 } }, // Seoul
  { code: 'KW', capital: { lat: 29.3759, lng: 47.9774 } }, // Kuwait City
  { code: 'KY', capital: { lat: 19.2866, lng: -81.3744 } }, // George Town
  { code: 'KZ', capital: { lat: 51.1605, lng: 71.4704 } }, // Astana
  { code: 'LA', capital: { lat: 17.9757, lng: 102.6331 } }, // Vientiane
  { code: 'LB', capital: { lat: 33.8938, lng: 35.5018 } }, // Beirut
  { code: 'LC', capital: { lat: 14.0101, lng: -60.9875 } }, // Castries
  { code: 'LI', capital: { lat: 47.141, lng: 9.5209 } }, // Vaduz
  { code: 'LK', capital: { lat: 6.9271, lng: 79.8612 } }, // Colombo
  { code: 'LR', capital: { lat: 6.3009, lng: -10.7969 } }, // Monrovia
  { code: 'LS', capital: { lat: -29.31, lng: 27.4869 } }, // Maseru
  { code: 'LT', capital: { lat: 54.6872, lng: 25.2797 } }, // Vilnius
  { code: 'LU', capital: { lat: 49.6116, lng: 6.1319 } }, // Luxembourg
  { code: 'LV', capital: { lat: 56.9496, lng: 24.1052 } }, // Riga
  { code: 'LY', capital: { lat: 32.8872, lng: 13.1913 } }, // Tripoli
  { code: 'MA', capital: { lat: 34.0209, lng: -6.8416 } }, // Rabat
  { code: 'MC', capital: { lat: 43.7384, lng: 7.4246 } }, // Monaco
  { code: 'MD', capital: { lat: 47.0105, lng: 28.8638 } }, // Chisinau
  { code: 'ME', capital: { lat: 42.4304, lng: 19.2594 } }, // Podgorica
  { code: 'MG', capital: { lat: -18.8792, lng: 47.5079 } }, // Antananarivo
  { code: 'MH', capital: { lat: 7.0897, lng: 171.3803 } }, // Majuro
  { code: 'MK', capital: { lat: 41.9981, lng: 21.4254 } }, // Skopje
  { code: 'ML', capital: { lat: 12.6392, lng: -8.0029 } }, // Bamako
  { code: 'MM', capital: { lat: 19.7633, lng: 96.0785 } }, // Naypyidaw
  { code: 'MN', capital: { lat: 47.8864, lng: 106.9057 } }, // Ulaanbaatar
  { code: 'MR', capital: { lat: 18.0735, lng: -15.9582 } }, // Nouakchott
  { code: 'MT', capital: { lat: 35.8989, lng: 14.5146 } }, // Valletta
  { code: 'MU', capital: { lat: -20.1609, lng: 57.5012 } }, // Port Louis
  { code: 'MV', capital: { lat: 4.1755, lng: 73.5093 } }, // Male
  { code: 'MW', capital: { lat: -13.9626, lng: 33.7741 } }, // Lilongwe
  { code: 'MX', capital: { lat: 19.4326, lng: -99.1332 } }, // Mexico City
  { code: 'MY', capital: { lat: 3.139, lng: 101.6869 } }, // Kuala Lumpur
  { code: 'MZ', capital: { lat: -25.9692, lng: 32.5732 } }, // Maputo
  { code: 'NA', capital: { lat: -22.5609, lng: 17.0658 } }, // Windhoek
  { code: 'NE', capital: { lat: 13.5128, lng: 2.1128 } }, // Niamey
  { code: 'NG', capital: { lat: 9.0765, lng: 7.3986 } }, // Abuja
  { code: 'NI', capital: { lat: 12.1149, lng: -86.2362 } }, // Managua
  { code: 'NL', capital: { lat: 52.3676, lng: 4.9041 } }, // Amsterdam
  { code: 'NO', capital: { lat: 59.9139, lng: 10.7522 } }, // Oslo
  { code: 'NP', capital: { lat: 27.7172, lng: 85.324 } }, // Kathmandu
  { code: 'NR', capital: { lat: -0.5477, lng: 166.9209 } }, // Yaren
  { code: 'NZ', capital: { lat: -41.2865, lng: 174.7762 } }, // Wellington
  { code: 'OM', capital: { lat: 23.588, lng: 58.3829 } }, // Muscat
  { code: 'PA', capital: { lat: 8.9824, lng: -79.5199 } }, // Panama City
  { code: 'PE', capital: { lat: -12.0464, lng: -77.0428 } }, // Lima
  { code: 'PG', capital: { lat: -9.4438, lng: 147.1803 } }, // Port Moresby
  { code: 'PH', capital: { lat: 14.5995, lng: 120.9842 } }, // Manila
  { code: 'PK', capital: { lat: 33.6844, lng: 73.0479 } }, // Islamabad
  { code: 'PL', capital: { lat: 52.2297, lng: 21.0122 } }, // Warsaw
  { code: 'PR', capital: { lat: 18.4655, lng: -66.1057 } }, // San Juan
  { code: 'PS', capital: { lat: 31.9038, lng: 35.2034 } }, // Ramallah
  { code: 'PT', capital: { lat: 38.7223, lng: -9.1393 } }, // Lisbon
  { code: 'PW', capital: { lat: 7.5006, lng: 134.6242 } }, // Ngerulmud
  { code: 'PY', capital: { lat: -25.2637, lng: -57.5759 } }, // Asuncion
  { code: 'QA', capital: { lat: 25.2854, lng: 51.531 } }, // Doha
  { code: 'RO', capital: { lat: 44.4268, lng: 26.1025 } }, // Bucharest
  { code: 'RS', capital: { lat: 44.7866, lng: 20.4489 } }, // Belgrade
  { code: 'RU', capital: { lat: 55.7558, lng: 37.6173 } }, // Moscow
  { code: 'RW', capital: { lat: -1.9579, lng: 30.1127 } }, // Kigali
  { code: 'SA', capital: { lat: 24.7136, lng: 46.6753 } }, // Riyadh
  { code: 'SB', capital: { lat: -9.4456, lng: 159.9729 } }, // Honiara
  { code: 'SC', capital: { lat: -4.6191, lng: 55.4513 } }, // Victoria
  { code: 'SD', capital: { lat: 15.5007, lng: 32.5599 } }, // Khartoum
  { code: 'SE', capital: { lat: 59.3293, lng: 18.0686 } }, // Stockholm
  { code: 'SG', capital: { lat: 1.3521, lng: 103.8198 } }, // Singapore
  { code: 'SI', capital: { lat: 46.0569, lng: 14.5058 } }, // Ljubljana
  { code: 'SK', capital: { lat: 48.1486, lng: 17.1077 } }, // Bratislava
  { code: 'SL', capital: { lat: 8.4657, lng: -13.2317 } }, // Freetown
  { code: 'SM', capital: { lat: 43.9424, lng: 12.4578 } }, // San Marino
  { code: 'SN', capital: { lat: 14.7167, lng: -17.4677 } }, // Dakar
  { code: 'SO', capital: { lat: 2.0469, lng: 45.3182 } }, // Mogadishu
  { code: 'SR', capital: { lat: 5.852, lng: -55.2038 } }, // Paramaribo
  { code: 'SS', capital: { lat: 4.8594, lng: 31.5713 } }, // Juba
  { code: 'ST', capital: { lat: 0.1864, lng: 6.6131 } }, // Sao Tome
  { code: 'SV', capital: { lat: 13.6929, lng: -89.2182 } }, // San Salvador
  { code: 'SY', capital: { lat: 33.5138, lng: 36.2765 } }, // Damascus
  { code: 'SZ', capital: { lat: -26.3054, lng: 31.1367 } }, // Mbabane
  { code: 'TD', capital: { lat: 12.1348, lng: 15.0557 } }, // N'Djamena
  { code: 'TG', capital: { lat: 6.1725, lng: 1.2314 } }, // Lome
  { code: 'TH', capital: { lat: 13.7563, lng: 100.5018 } }, // Bangkok
  { code: 'TJ', capital: { lat: 38.5598, lng: 68.787 } }, // Dushanbe
  { code: 'TL', capital: { lat: -8.5569, lng: 125.5603 } }, // Dili
  { code: 'TM', capital: { lat: 37.9601, lng: 58.3261 } }, // Ashgabat
  { code: 'TN', capital: { lat: 36.8065, lng: 10.1815 } }, // Tunis
  { code: 'TO', capital: { lat: -21.139, lng: -175.2049 } }, // Nuku'alofa
  { code: 'TR', capital: { lat: 39.9334, lng: 32.8597 } }, // Ankara
  { code: 'TT', capital: { lat: 10.6918, lng: -61.2225 } }, // Port of Spain
  { code: 'TV', capital: { lat: -8.5211, lng: 179.1942 } }, // Funafuti
  { code: 'TW', capital: { lat: 25.033, lng: 121.5654 } }, // Taipei
  { code: 'TZ', capital: { lat: -6.1629, lng: 35.7516 } }, // Dodoma
  { code: 'UA', capital: { lat: 50.4501, lng: 30.5234 } }, // Kyiv
  { code: 'UG', capital: { lat: 0.3476, lng: 32.5825 } }, // Kampala
  { code: 'US', capital: { lat: 38.9072, lng: -77.0369 } }, // Washington, D.C.
  { code: 'UY', capital: { lat: -34.9011, lng: -56.1645 } }, // Montevideo
  { code: 'UZ', capital: { lat: 41.2995, lng: 69.2401 } }, // Tashkent
  { code: 'VA', capital: { lat: 41.9029, lng: 12.4534 } }, // Vatican City
  { code: 'VC', capital: { lat: 13.1587, lng: -61.2248 } }, // Kingstown
  { code: 'VE', capital: { lat: 10.4806, lng: -66.9036 } }, // Caracas
  { code: 'VN', capital: { lat: 21.0285, lng: 105.8542 } }, // Hanoi
  { code: 'VU', capital: { lat: -17.7333, lng: 168.3273 } }, // Port Vila
  { code: 'WS', capital: { lat: -13.8506, lng: -171.7513 } }, // Apia
  { code: 'XK', capital: { lat: 42.6629, lng: 21.1655 } }, // Pristina (Kosovo)
  { code: 'YE', capital: { lat: 15.3694, lng: 44.191 } }, // Sanaa
  { code: 'ZA', capital: { lat: -25.7479, lng: 28.2293 } }, // Pretoria
  { code: 'ZM', capital: { lat: -15.3875, lng: 28.3228 } }, // Lusaka
  { code: 'ZW', capital: { lat: -17.8292, lng: 31.0522 } }, // Harare
];
